import Phaser from "phaser";
import {
  TILE_SIZE,
  TOP_BAR_HEIGHT,
  TowerStats,
  TowerLevelStats,
  getTowerLevelStats,
  getTowerMaxLevel,
  getTowerUpgradeCost
} from "../config";
import { Enemy, DamageType } from "./Enemy";
import { computeMetaModifiers, MetaModifiers } from "../systems/SkillTree";
import { Projectile, ProjectileBehavior } from "./Projectile";
import { sound } from "../systems/Sound";
import {
  chainLightning,
  holySmite,
  levelUpEffect,
  lightningSparks,
  muzzleFlash
} from "../assets/effects";

/**
 * Global crit tuning. Deliberately not per-tower config: crits are a feel
 * mechanic layered over every tower equally, not a balance lever. The values
 * are modest so DPS maths stays roughly where it was (12% * 0.9 extra ≈ +11%).
 */
const CRIT_CHANCE = 0.12;
const CRIT_MULTIPLIER = 1.9;

/** Damage type per tower key. */
function damageTypeFor(key: string): DamageType {
  switch (key) {
    case "mage":
    case "lightning":
    case "holy":
      return "magic";
    case "crossbow":
      return "pierce";
    default:
      return "physical";
  }
}

/** Projectile texture per tower key. */
function projectileTextureFor(key: string): string {
  switch (key) {
    case "archer":     return "proj-arrow";
    case "cannon":     return "proj-cannon";
    case "mage":       return "proj-mage";
    case "frost":      return "proj-frost";
    case "lightning":  return "proj-bolt";
    case "poison":     return "proj-poison";
    case "crossbow":   return "proj-bolt";
    case "holy":       return "proj-mage";
    default:           return "proj-arrow";
  }
}

/**
 * Per-tower animation config. All textures listed here are guaranteed to exist
 * (either painted PNGs in /public/projectiles or procedurally generated by
 * generateTextures.ts). Adding a new tower? Add an entry here — no other
 * code change is needed for the visuals to come alive.
 *
 *  turret      = painted weapon sprite mounted on the tower base. Textures come
 *                in three material tiers (`${texBase}-t1..t3`, levels 1-4/5-8/
 *                9-12), painted pointing RIGHT (+X, Phaser's rotation=0).
 *                mode "rotate" aims the sprite at the target and recoils along
 *                the aim axis (ballista, cannon barrel); mode "flip" keeps an
 *                upright figure that only mirrors left/right (archer). Where a
 *                `-fired` pose exists it is shown for firedMs on each shot.
 *  idleSpark   = pixel-particle bursts emitted at intervalIdleMs (slow when no
 *                target) or intervalCombatMs (fast when a target is in range).
 *  smoke       = rising tinted pixel particles, same cadence model as sparks.
 *  fireFlash   = additive tint pulse on the tower base sprite when firing.
 *  fireBurst   = expanding ring drawn at the tower on fire (Frost shockwave,
 *                Holy halo, etc.).
 */
type TurretCfg = {
  texBase: string;
  mode: "rotate" | "flip";
  /** Display size (px) of the square turret canvas at level 1. */
  size: number;
  /** Extra display growth of the weapon from level 1 to max (0.22 = +22%). */
  sizeGrowth: number;
  /** Pivot within the sprite: rotation origin (rotate) / foot point (flip). */
  ox: number; oy: number;
  /** Mount height above the tile bottom, as a fraction of the base sprite's display height. */
  mountFrac: number;
  aimSpeed: number; recoil: number; pulse: number; restAngle: number;
  /** How long the painted `-fired` pose stays up, where the delivery includes one. */
  firedMs?: number;
};
type SparkCfg  = { color: number; intervalIdleMs: number; intervalCombatMs: number; rise?: boolean; };
type FlashCfg  = { color: number; durationMs: number; };
type BurstCfg  = { color: number; radius: number; durationMs: number; };
type TowerAnim = { turret?: TurretCfg; idleSpark?: SparkCfg; smoke?: SparkCfg; fireFlash?: FlashCfg; fireBurst?: BurstCfg; };

const TOWER_ANIM: Record<string, TowerAnim> = {
  archer:    { turret: { texBase: "turret-archer",   mode: "flip",   size: 30, sizeGrowth: 0.06, ox: 0.5,  oy: 0.98, mountFrac: 0.68,
                         aimSpeed: 0,  recoil: 1.5, pulse: 1.06, restAngle: 0, firedMs: 130 } },
  cannon:    { turret: { texBase: "turret-cannon",   mode: "rotate", size: 38, sizeGrowth: 0.22, ox: 0.30, oy: 0.52, mountFrac: 0.52,
                         aimSpeed: 7,  recoil: 7,   pulse: 1.0,  restAngle: -0.55 },
               fireFlash: { color: 0xff8844, durationMs: 90 } },
  mage:      { fireFlash: { color: 0xb070ff, durationMs: 80 } },
  frost:     { fireFlash: { color: 0xb8e0ff, durationMs: 70 },
               fireBurst: { color: 0xb8e0ff, radius: 36, durationMs: 280 } },
  lightning: { idleSpark: { color: 0xfff099, intervalIdleMs: 1800, intervalCombatMs: 350 },
               fireFlash: { color: 0xfff099, durationMs: 70 } },
  poison:    { smoke:     { color: 0x6cc06c, intervalIdleMs: 750,  intervalCombatMs: 220, rise: true },
               fireFlash: { color: 0x88e088, durationMs: 100 } },
  crossbow:  { turret: { texBase: "turret-crossbow", mode: "rotate", size: 36, sizeGrowth: 0.22, ox: 0.35, oy: 0.5,  mountFrac: 0.58,
                         aimSpeed: 12, recoil: 4,   pulse: 1.0,  restAngle: -0.55, firedMs: 130 } },
  holy:      {} // Sanctum: aura ring (constructor) + soft upward light shaft on smite (see fireSmite).
};

/**
 * Measured art metrics for tower sets whose paintings don't fill their 96px
 * canvas evenly. Without these, a level whose art fills less of the canvas
 * renders visibly *smaller* than the level below it (crossbow 8, cannon 4/7),
 * and the mounted weapon floats above or sinks into the painted deck.
 *
 *  contentH  height in px of the opaque pixels inside each level's 96px
 *            canvas (regenerate with `node scripts/measure-tower-art.mjs`
 *            after any asset swap).
 *  contentW  the same measurement across the x axis.
 *  ramp      target on-screen content height in tiles at level 1 -> max;
 *            display size is contentH-normalised onto this line so upgrades
 *            grow strictly monotonically.
 *  maxWidth  widest the painted content may render, in tiles at level 1 -> max.
 *            Height normalisation scales the whole square canvas, so the cap
 *            prevents a squat painting from blowing up sideways and exposes
 *            malformed art by making it render shorter than its height ramp.
 *  deckY     canvas y of the painted turret socket / standing deck per level,
 *            read off the art by hand; the weapon pivot is anchored there.
 */
type TowerArtMetrics = {
  contentH?: number[];
  contentW?: number[];
  ramp?: [number, number];
  maxWidth?: [number, number];
  deckY?: number[];
};
const TOWER_ART: Record<string, TowerArtMetrics> = {
  crossbow: {
    contentH: [70, 71, 77, 88, 87, 85, 89, 90, 90, 90, 90, 90],
    contentW: [90, 90, 90, 90, 90, 90, 90, 89, 87, 81, 79, 77],
    ramp: [0.74, 1.22],
    maxWidth: [1.00, 1.10],
    deckY: [38, 34, 32, 27, 20, 24, 27, 24, 46, 35, 36, 34]
  },
  cannon: {
    contentH: [66, 73, 75, 72, 84, 78, 72, 70, 74, 76, 78, 81],
    contentW: [90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90],
    ramp: [0.71, 1.10],
    maxWidth: [1.10, 1.32],
    deckY: [44, 42, 31, 40, 40, 52, 56, 38, 44, 36, 40, 40]
  },
  archer: {
    // Content height is a flat 90px across all levels, so the default growth
    // formula already yields a clean ramp — only the deck needs anchoring.
    deckY: [26, 28, 30, 32, 33, 32, 30, 30, 33, 32, 34, 34]
  },
  // The magic towers have no mounted turret (no deckY), but their canvas fill
  // still varies enough to stall or dip the growth ramp (frost 3 shrinks,
  // poison/holy repeat heights). Ramps preserve each set's level-1/12 look.
  // mage is a flat 90px like archer and needs no entry at all.
  frost: {
    contentH: [92, 93, 85, 93, 92, 92, 93, 93, 90, 90, 90, 90],
    contentW: [63, 67, 77, 60, 71, 60, 50, 67, 55, 45, 52, 69],
    ramp: [0.98, 1.22],
    maxWidth: [0.95, 1.15]
  },
  lightning: {
    contentH: [90, 92, 93, 90, 92, 92, 93, 92, 92, 93, 93, 92],
    contentW: [77, 51, 68, 56, 73, 56, 49, 76, 54, 67, 56, 82],
    ramp: [0.96, 1.25],
    maxWidth: [0.95, 1.20]
  },
  poison: {
    contentH: [73, 93, 92, 92, 91, 92, 91, 92, 92, 93, 92, 90],
    contentW: [75, 72, 61, 58, 68, 73, 78, 53, 67, 73, 68, 87],
    ramp: [0.78, 1.22],
    maxWidth: [0.95, 1.25]
  },
  holy: {
    contentH: [85, 92, 90, 90, 93, 92, 91, 92, 90, 92, 91, 93],
    contentW: [75, 65, 77, 69, 72, 70, 81, 75, 85, 61, 75, 67],
    ramp: [0.90, 1.26],
    maxWidth: [0.95, 1.25]
  }
};

export class Tower extends Phaser.GameObjects.Container {
  private sprite: Phaser.GameObjects.Image;
  /** Animation config for this tower (looked up by stats.key in constructor). */
  private anim: TowerAnim = {};
  /** Optional turret indicator (proj-* sprite) that aims at the current target. */
  private turret?: Phaser.GameObjects.Image;
  /** Stable turret scale used as the origin for every fire pulse. */
  private turretBaseScaleX = 1;
  private turretBaseScaleY = 1;
  /** Current turret aim angle (radians). Lerps toward target each frame. */
  private turretAngle = -Math.PI / 2;
  /** Recoil offset along aim direction in pixels (decays exponentially). */
  private turretRecoil = 0;
  /** Pending revert timer for the turret's painted `-fired` pose. */
  private firedTimer?: Phaser.Time.TimerEvent;
  /** Idle/combat particle emission accumulator (seconds). */
  private sparkTimer = Math.random() * 2;
  private smokeTimer = Math.random() * 2;
  /** True when this tower has a target this frame (drives faster idle FX). */
  private inCombat = false;
  private cooldown = 0;
  private rangeCircle?: Phaser.GameObjects.Arc;
  private auraRing?: Phaser.GameObjects.Arc | Phaser.GameObjects.Image;
  public level = 1;
  private _totalInvested: number;
  private mods: MetaModifiers = computeMetaModifiers();

  /** Idle anim phase (per-tower offset for variety). */
  private animPhase = Math.random() * Math.PI * 2;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    public stats: TowerStats,
    private enemiesGetter: () => Enemy[],
    private spawnProjectile: (p: Projectile) => void,
    private towersGetter?: () => Tower[],
    initialCost: number = stats.levels[0].cost
  ) {
    super(scene, x, y);
    this._totalInvested = initialCost;

    // Base sprite is anchored at the tile's bottom edge and grows with the
    // level (see baseSize) so upgrades read as taller buildings, overlapping
    // the tile above like classic TD towers.
    this.sprite = scene.add.image(0, TILE_SIZE / 2, this.textureKey());
    this.sprite.setOrigin(0.5, 1);
    this.applyBaseSize();
    this.add(this.sprite);

    // Mount per-tower animation config + optional painted weapon turret.
    this.anim = TOWER_ANIM[stats.key] ?? {};
    if (this.anim.turret) {
      this.turretAngle = this.anim.turret.restAngle;
      this.mountTurret();
    }

    scene.add.existing(this);
    this.setDepth(y + TILE_SIZE / 2);
    this.setSize(TILE_SIZE, TILE_SIZE);
    this.setInteractive(
      new Phaser.Geom.Rectangle(0, 0, TILE_SIZE, TILE_SIZE),
      Phaser.Geom.Rectangle.Contains
    );
    this.on("pointerover", () => {
      // While a build ghost is active, the placement preview owns the range
      // ring; don't stack this tower's (larger, upgraded) ring on top of it.
      const placement = scene.registry.get("placement") as { isPlacing: boolean } | undefined;
      if (placement?.isPlacing) return;
      this.showRange(true);
    });
    this.on("pointerout", () => this.showRange(false));
    this.on("pointerdown", (_p: Phaser.Input.Pointer, _lx: number, _ly: number, evt: Phaser.Types.Input.EventData) => {
      evt.stopPropagation();
      scene.events.emit("tower:selected", this);
    });

    // Persistent aura ring for holy.
    this.createAuraVisual();
  }

  get current(): TowerLevelStats { return getTowerLevelStats(this.stats, this.level); }
  get maxLevel(): number { return getTowerMaxLevel(this.stats); }
  get isMaxLevel(): boolean { return this.level >= this.maxLevel; }
  get nextUpgradeCost(): number | null { return getTowerUpgradeCost(this.stats, this.level); }
  get totalInvested(): number { return this._totalInvested; }

  /** Effective range after meta modifiers. */
  effectiveRange(): number { return this.current.range * this.mods.rangeMult; }
  /** Apply meta damage modifiers + per-level upgrade boost. */
  effectiveDamage(base: number): number {
    const upgradeBonus = Math.pow(this.mods.upgradeDamageMult, Math.max(0, this.level - 1));
    return base * this.mods.damageMult * upgradeBonus;
  }

  /**
   * Roll a shot's damage, including a chance to crit. Crits are what turn a
   * steady stream of identical numbers into a readable rhythm — they get their
   * own colour, a bigger number, a stagger and a frame of hit-stop.
   */
  private rollDamage(base: number): { amount: number; crit: boolean } {
    const amount = this.effectiveDamage(base);
    const crit = Math.random() < CRIT_CHANCE;
    return { amount: crit ? amount * CRIT_MULTIPLIER : amount, crit };
  }

  private textureKey(): string { return `tower-${this.stats.key}-${this.level}`; }

  /** Level growth 0..1 across the upgrade run. */
  private growth(): number {
    return this.maxLevel > 1 ? (this.level - 1) / (this.maxLevel - 1) : 0;
  }

  /** Base display size in px: 1.02 tiles at level 1 growing to 1.30 at max.
   *  Sets with measured metrics normalise the *content* height onto their
   *  ramp instead, so canvas-fill differences between levels can't make an
   *  upgrade render smaller than its predecessor — then clamp to maxWidth so
   *  that normalisation can't blow a squat painting up sideways either. */
  private baseSize(): number {
    return Math.min(this.artSize(), this.headroom());
  }

  private artSize(): number {
    const art = TOWER_ART[this.stats.key];
    const g = this.growth();
    if (art?.contentH && art.ramp) {
      const i = Math.min(this.level, art.contentH.length) - 1;
      const tiles = art.ramp[0] + (art.ramp[1] - art.ramp[0]) * g;
      const fitH = TILE_SIZE * tiles * 96 / art.contentH[i];
      if (art.contentW && art.maxWidth) {
        const wTiles = art.maxWidth[0] + (art.maxWidth[1] - art.maxWidth[0]) * g;
        const fitW = TILE_SIZE * wTiles * 96 / art.contentW[Math.min(this.level, art.contentW.length) - 1];
        return Math.min(fitH, fitW);
      }
      return fitH;
    }
    return TILE_SIZE * (1.02 + 0.28 * g);
  }

  /** Room between the tile's bottom edge and the opaque HUD bar. The art grows
   *  upward from that edge, so on the top row a max-level building would reach
   *  behind the bar and have its roof sliced off flat (measured: 7px at level
   *  12). Capping keeps the whole tower visible; every row below has far more
   *  headroom than the art can use, so only the top row is ever affected. */
  private headroom(): number {
    return this.y + TILE_SIZE / 2 - TOP_BAR_HEIGHT;
  }

  private applyBaseSize(): void {
    const s = this.baseSize();
    this.sprite.setDisplaySize(s, s);
  }

  /** Material tier of the mounted weapon: wood 1-4, iron 5-8, gilded 9-12. */
  private turretTier(): number {
    const third = Math.max(1, this.maxLevel / 3);
    return Math.min(3, 1 + Math.floor((this.level - 1) / third));
  }

  private turretTexture(fired = false): string {
    return `${this.anim.turret!.texBase}-t${this.turretTier()}${fired ? "-fired" : ""}`;
  }

  /** Turret mount point (container-local y): the painted socket/deck of the
   *  current level's art where measured, else a fixed fraction of the base. */
  private mountY(): number {
    const deckY = TOWER_ART[this.stats.key]?.deckY?.[this.level - 1];
    if (deckY != null) return TILE_SIZE / 2 - this.baseSize() * (96 - deckY) / 96;
    return TILE_SIZE / 2 - this.baseSize() * this.anim.turret!.mountFrac;
  }

  /** Turret display size: grows with level so the weapon keeps pace with the base. */
  private turretSize(): number {
    const cfg = this.anim.turret!;
    return cfg.size * (1 + cfg.sizeGrowth * this.growth());
  }

  /** Vertical muzzle origin (container-local y) for towers without a turret. */
  private muzzleY(): number {
    return TILE_SIZE / 2 - this.baseSize() * 0.72;
  }

  /** Create the turret, or re-skin it after an upgrade changed the tier/size. */
  private mountTurret(): void {
    const cfg = this.anim.turret;
    if (!cfg) return;
    if (!this.turret) {
      this.turret = this.scene.add.image(0, 0, this.turretTexture());
      this.add(this.turret);
    } else {
      this.turret.setTexture(this.turretTexture());
    }
    this.turret.setOrigin(cfg.ox, cfg.oy);
    const ts = this.turretSize();
    this.turret.setDisplaySize(ts, ts);
    this.turretBaseScaleX = this.turret.scaleX;
    this.turretBaseScaleY = this.turret.scaleY;
    this.turret.setRotation(cfg.mode === "rotate" ? this.turretAngle : 0);
    this.turret.setPosition(0, this.mountY());
  }

  upgrade(): boolean {
    if (this.isMaxLevel) return false;
    const cost = this.nextUpgradeCost;
    if (cost == null) return false;
    this._totalInvested += cost;
    this.level++;
    this.sprite.setTexture(this.textureKey());
    this.applyBaseSize();
    if (this.anim.turret) this.mountTurret();
    if (this.rangeCircle) {
      this.rangeCircle.destroy();
      this.rangeCircle = undefined;
      this.showRange(true);
    }
    if (this.auraRing) { this.auraRing.destroy(); this.auraRing = undefined; }
    this.createAuraVisual();
    levelUpEffect(this.scene, this.x, this.y, this.level);
    this.scene.events.emit("tower:upgraded", this);
    sound.playUpgrade();
    return true;
  }

  destroy(fromScene?: boolean): void {
    this.firedTimer?.remove();
    this.firedTimer = undefined;
    this.rangeCircle?.destroy();
    this.rangeCircle = undefined;
    this.auraRing?.destroy();
    this.auraRing = undefined;
    super.destroy(fromScene);
  }

  showRange(show: boolean): void {
    if (show) {
      if (!this.rangeCircle) {
        this.rangeCircle = this.scene.add
          .circle(this.x, this.y, this.effectiveRange(), 0xffffff, 0.08)
          .setStrokeStyle(1, 0xffffff, 0.5);
      }
    } else {
      this.rangeCircle?.destroy();
      this.rangeCircle = undefined;
    }
  }

  /** Effective fire rate after summing aura bonuses from nearby holy towers. */
  private effectiveFireRate(): number {
    const base = this.current.fireRate * this.mods.fireRateMult;
    let bonus = 0;
    if (this.towersGetter) {
      for (const other of this.towersGetter()) {
        if (other === this) continue;
        const auraBonus = other.current.auraFireRateBonus;
        const auraR = other.current.auraRadius;
        if (!auraBonus || !auraR) continue;
        const dx = other.x - this.x;
        const dy = other.y - this.y;
        if (dx * dx + dy * dy <= auraR * auraR) bonus += auraBonus;
      }
    }
    bonus = Math.min(0.6, bonus);
    // Apply Commander "Rally" buff if active (read from scene registry to avoid coupling).
    const ability = this.scene.registry.get("abilityController") as { rallyMult?: (t: number) => number } | undefined;
    const rally = ability?.rallyMult ? ability.rallyMult(this.scene.time.now) : 1;
    return base * (1 + bonus) * rally;
  }

  update(time: number, deltaMs: number): void {
    const dt = deltaMs / 1000;

    this.animPhase += dt * 2;

    // Find the current target once per frame so aim tracking + firing share it.
    const target = this.findTarget();
    this.inCombat = !!target;

    // Turret aim + recoil. Rotating weapons track the target; flip figures
    // stay upright and only mirror to face it (a rotating archer reads as a
    // ragdoll, not a marksman).
    if (this.turret && this.anim.turret) {
      const cfg = this.anim.turret;
      if (cfg.mode === "rotate") {
        // An idle turret holds its last aim. Swinging back to restAngle between
        // targets made every tower rotate constantly whenever the lane was empty.
        if (target) {
          const desired = Math.atan2(target.y - (this.y + this.mountY()), target.x - this.x);
          // Shortest-arc lerp toward desired angle at cfg.aimSpeed rad/s.
          const diff = Phaser.Math.Angle.Wrap(desired - this.turretAngle);
          const maxStep = cfg.aimSpeed * dt;
          if (Math.abs(diff) <= maxStep) this.turretAngle = desired;
          else this.turretAngle += Math.sign(diff) * maxStep;
        }
        this.turret.setRotation(this.turretAngle);
        this.turret.x = Math.cos(this.turretAngle) * -this.turretRecoil;
        this.turret.y = this.mountY() + Math.sin(this.turretAngle) * -this.turretRecoil;
      } else {
        if (target) this.turret.setFlipX(target.x < this.x);
        this.turret.x = 0;
        this.turret.y = this.mountY() + this.turretRecoil;
      }
      this.turretRecoil *= Math.pow(0.001, dt); // ~99.9% decay per second
      if (this.turretRecoil < 0.05) this.turretRecoil = 0;
    }

    // Idle / combat particle emission (sparks + smoke).
    if (this.anim.idleSpark) this.tickParticle(this.anim.idleSpark, dt, "spark");
    if (this.anim.smoke)     this.tickParticle(this.anim.smoke,     dt, "smoke");

    // Aura visual pulse.
    if (this.auraRing) {
      this.auraRing.setAlpha(0.04 + (Math.sin(this.animPhase * 1.5) + 1) * 0.04);
    }

    this.cooldown -= dt;
    if (this.cooldown > 0) return;
    if (!target) return;

    this.fireAt(target);
    this.cooldown = 1 / this.effectiveFireRate();
    // For aura towers, "firing" also re-pulses the visual aura.
    if (this.auraRing) this.auraRing.setAlpha(0.18);
  }

  /** Emit a single tinted pixel particle (spark) or rising puff (smoke). */
  private tickParticle(cfg: SparkCfg, dt: number, kind: "spark" | "smoke"): void {
    const interval = (this.inCombat ? cfg.intervalCombatMs : cfg.intervalIdleMs) / 1000;
    const acc = kind === "spark" ? "sparkTimer" : "smokeTimer";
    this[acc] += dt;
    if (this[acc] < interval) return;
    this[acc] = 0;
    // Prefer the painted idle VFX, with the original rectangle as fallback.
    const ox = (Math.random() - 0.5) * 14;
    const oy = -TILE_SIZE * 0.18 + (Math.random() - 0.5) * 6;
    const size = kind === "smoke" ? 6 : 3;
    const textureKey = kind === "spark" ? "vfx-lightning-sparks" : "vfx-poison-muzzle";
    const p: Phaser.GameObjects.Image | Phaser.GameObjects.Rectangle = this.scene.textures.exists(textureKey)
      ? this.scene.add.image(this.x + ox, this.y + oy, textureKey)
          .setDisplaySize(kind === "smoke" ? 18 : 13, kind === "smoke" ? 18 : 13).setAlpha(0.72).setDepth(this.depth + 1)
      : this.scene.add.rectangle(this.x + ox, this.y + oy, size, size, cfg.color, 0.85).setDepth(this.depth + 1);
    const dx = kind === "smoke" ? (Math.random() - 0.5) * 6 : (Math.random() - 0.5) * 18;
    const dy = kind === "smoke" ? -22 - Math.random() * 10 : (Math.random() - 0.5) * 18;
    this.scene.tweens.add({
      targets: p,
      x: p.x + dx,
      y: p.y + dy,
      alpha: 0,
      scale: kind === "smoke" ? 1.6 : 0.4,
      duration: kind === "smoke" ? 900 : 320,
      ease: kind === "smoke" ? "Sine.Out" : "Cubic.Out",
      onComplete: () => p.destroy()
    });
  }

  /** Brief additive flash overlay above the tower base. Does not modify the
   *  base sprite tint (which would visibly snap on/off and flicker on rapid
   *  fire). Instead we spawn a same-texture overlay, tint *that*, fade it
   *  smoothly, and discard. */
  private flashBase(color: number, durationMs: number): void {
    const overlay = this.scene.add.image(this.x, this.y + TILE_SIZE / 2, this.textureKey());
    overlay.setOrigin(0.5, 1);
    overlay.setDisplaySize(this.sprite.displayWidth, this.sprite.displayHeight);
    overlay.setTint(color);
    overlay.setBlendMode(Phaser.BlendModes.ADD);
    overlay.setAlpha(0.55);
    overlay.setDepth(this.depth + 1);
    this.scene.tweens.add({
      targets: overlay,
      alpha: 0,
      duration: durationMs,
      ease: "Quad.Out",
      onComplete: () => overlay.destroy()
    });
  }

  /** Expanding ring effect at the tower (frost shockwave, holy halo). */
  private burstRing(color: number, radius: number, durationMs: number): void {
    const ring = this.scene.add.circle(this.x, this.y, 4, undefined, 0)
      .setStrokeStyle(2, color, 0.9)
      .setDepth(this.depth + 2);
    this.scene.tweens.add({
      targets: ring,
      radius,
      alpha: 0,
      duration: durationMs,
      ease: "Cubic.Out",
      onComplete: () => ring.destroy()
    });
  }

  /** Persistent Sanctum speed aura, using painted VFX when available. */
  private createAuraVisual(): void {
    const radius = this.current.auraRadius;
    if (!radius) return;
    if (this.scene.textures.exists("vfx-holy-speed-aura")) {
      this.auraRing = this.scene.add.image(this.x, this.y, "vfx-holy-speed-aura")
        .setDisplaySize(radius * 2, radius * 2)
        .setAlpha(0.12)
        .setDepth(10)
        .setBlendMode(Phaser.BlendModes.ADD);
      return;
    }
    this.auraRing = this.scene.add.circle(this.x, this.y, radius, 0xfff2a8, 0.05)
      .setStrokeStyle(1, 0xfff2a8, 0.4)
      .setDepth(10);
  }

  private findTarget(): Enemy | null {
    let best: Enemy | null = null;
    let bestProgress = -1;
    const range = this.effectiveRange();
    const r2 = range * range;
    // Anti-air capable towers: mage / lightning / holy / crossbow.
    const canHitFlying = this.stats.key === "mage" || this.stats.key === "lightning" ||
                         this.stats.key === "holy" || this.stats.key === "crossbow";
    for (const e of this.enemiesGetter()) {
      if (!e.alive) continue;
      if (e.stats.flying && !canHitFlying) continue;
      const dx = e.x - this.x;
      const dy = e.y - this.y;
      const distSq = dx * dx + dy * dy;
      if (distSq > r2) continue;
      // Invisible enemies require *this* tower to be within `invisRadius`.
      if (e.stats.invisible && e.stats.invisRadius) {
        if (distSq > e.stats.invisRadius * e.stats.invisRadius) continue;
      }
      const progress = e.waypointIdx ?? 0;
      if (progress > bestProgress) {
        bestProgress = progress;
        best = e;
      }
    }
    return best;
  }

  private fireAt(target: Enemy): void {
    const cur = this.current;
    const key = this.stats.key;
    const dmgType = damageTypeFor(key);
    // The shot leaves from the painted weapon: the turret's tip for rotating
    // weapons, the figure's bow for the archer, the base's upper third
    // otherwise — never from the tower's centre.
    let aimAngle: number;
    let launchX: number;
    let launchY: number;
    const turretCfg = this.anim.turret;
    if (this.turret && turretCfg && turretCfg.mode === "rotate") {
      const my = this.y + this.mountY();
      aimAngle = Math.atan2(target.y - my, target.x - this.x);
      const tip = this.turret.displayWidth * (1 - turretCfg.ox) - 2;
      launchX = this.x + Math.cos(aimAngle) * tip;
      launchY = my + Math.sin(aimAngle) * tip;
    } else if (this.turret && turretCfg) {
      const face = target.x < this.x ? -1 : 1;
      launchX = this.x + face * 8;
      launchY = this.y + this.mountY() - this.turret.displayHeight * 0.55;
      aimAngle = Math.atan2(target.y - launchY, target.x - launchX);
    } else {
      const my = this.y + this.muzzleY();
      aimAngle = Math.atan2(target.y - my, target.x - this.x);
      launchX = this.x + Math.cos(aimAngle) * 12;
      launchY = my + Math.sin(aimAngle) * 12;
    }
    sound.playFire(key);
    // The flash and projectile share the exact same calculated muzzle point.
    // This keeps the firing cue attached to the shot instead of flashing at
    // the tower centre while the projectile travels in another direction.
    muzzleFlash(this.scene, launchX, launchY, key, this.stats.color, aimAngle);

    // Apply per-tower fire animations: turret recoil/pulse, painted fired
    // pose, base flash, ring burst.
    if (this.turret && this.anim.turret) {
      this.turretRecoil = this.anim.turret.recoil;
      const firedMs = this.anim.turret.firedMs;
      if (firedMs && this.scene.textures.exists(this.turretTexture(true))) {
        this.turret.setTexture(this.turretTexture(true));
        this.firedTimer?.remove();
        this.firedTimer = this.scene.time.delayedCall(firedMs, () => {
          this.turret?.setTexture(this.turretTexture());
        });
      }
      const peak = this.anim.turret.pulse;
      if (peak !== 1.0) {
        this.scene.tweens.killTweensOf(this.turret);
        // A high-level crossbow can fire again before the previous yoyo has
        // returned to rest. Always restart from the original display scale;
        // using the current mid-pulse scale here compounds its size per shot.
        this.turret.setScale(this.turretBaseScaleX, this.turretBaseScaleY);
        this.scene.tweens.add({
          targets: this.turret,
          scaleX: this.turretBaseScaleX * peak,
          scaleY: this.turretBaseScaleY * peak,
          duration: 60,
          yoyo: true,
          ease: "Quad.Out"
        });
      }
    }
    if (this.anim.fireFlash) this.flashBase(this.anim.fireFlash.color, this.anim.fireFlash.durationMs);
    if (this.anim.fireBurst) this.burstRing(this.anim.fireBurst.color, this.anim.fireBurst.radius, this.anim.fireBurst.durationMs);

    // Tower-specific dispatch.
    if (key === "lightning") {
      this.fireChain(target, cur);
      return;
    }
    if (key === "holy") {
      this.fireSmite(target, cur);
      return;
    }

    let behavior: ProjectileBehavior = "homing";
    let impactEffect: "explosion" | "frost" | "poison" | "none" = "none";
    if (key === "cannon") { behavior = "splash"; impactEffect = "explosion"; }
    if (key === "frost")  { behavior = "splash"; impactEffect = "frost"; }
    if (key === "poison") { behavior = "splash"; impactEffect = "poison"; }
    if (key === "crossbow") behavior = "pierce";

    const roll = this.rollDamage(cur.damage);
    const p = new Projectile(this.scene, launchX, launchY, {
      textureKey: projectileTextureFor(key),
      target,
      speed: cur.projectileSpeed,
      damage: roll.amount,
      crit: roll.crit,
      damageType: dmgType,
      behavior,
      splashRadius: cur.splashRadius,
      pierceCount: cur.pierceCount,
      sourceKey: key,
      applySlow: cur.slowFactor != null && cur.slowDurationMs != null
        ? { factor: cur.slowFactor, durationMs: cur.slowDurationMs }
        : undefined,
      applyDot: cur.dotDps != null && cur.dotDurationMs != null
        ? { dps: cur.dotDps, durationMs: cur.dotDurationMs }
        : undefined,
      impactEffect,
      enemiesGetter: this.enemiesGetter
    });
    this.spawnProjectile(p);
  }

  private fireChain(target: Enemy, cur: TowerLevelStats): void {
    const jumps = cur.chainCount ?? 1;
    const jumpRange = cur.chainRange ?? 100;
    const dmgType = damageTypeFor("lightning");
    const my = this.y + this.muzzleY();
    let prev: Enemy | { x: number; y: number } = { x: this.x, y: my };
    const hit = new Set<Enemy>();
    let curTarget: Enemy | null = target;
    lightningSparks(this.scene, this.x, my, 30);
    for (let i = 0; i < jumps && curTarget; i++) {
      chainLightning(this.scene, prev.x, prev.y, curTarget.x, curTarget.y, 0xffe070);
      lightningSparks(this.scene, curTarget.x, curTarget.y, i === 0 ? 38 : 30);
      // Only the first link can crit — otherwise a long chain sprays crits.
      const chainRoll = i === 0 ? this.rollDamage(cur.damage) : { amount: this.effectiveDamage(cur.damage), crit: false };
      curTarget.takeDamage(chainRoll.amount * Math.pow(0.85, i), dmgType, false, chainRoll.crit);
      hit.add(curTarget);
      prev = curTarget;
      // Find next nearest unhit enemy within jumpRange.
      let next: Enemy | null = null;
      let bestD2 = jumpRange * jumpRange;
      for (const e of this.enemiesGetter()) {
        if (!e.alive || hit.has(e)) continue;
        const dx = e.x - curTarget.x;
        const dy = e.y - curTarget.y;
        const d2 = dx * dx + dy * dy;
        if (d2 < bestD2) { bestD2 = d2; next = e; }
      }
      curTarget = next;
    }
  }

  private fireSmite(target: Enemy, cur: TowerLevelStats): void {
    holySmite(this.scene, target.x, target.y);
    const roll = this.rollDamage(cur.damage);
    target.takeDamage(roll.amount, "magic", false, roll.crit);
    // Soft upward channel beam from the sanctum's roof — narrow, fades fast,
    // additive feel via warm gold. No tint on the base sprite (looked too harsh).
    const beam = this.scene.add.rectangle(this.x, this.y + TILE_SIZE / 2 - this.baseSize() * 0.9, 6, 32, 0xfff2a8, 0.55)
      .setOrigin(0.5, 1)
      .setDepth(this.depth + 1);
    this.scene.tweens.add({
      targets: beam,
      alpha: 0,
      scaleY: 1.8,
      duration: 260,
      ease: "Quad.Out",
      onComplete: () => beam.destroy()
    });
  }
}
